import { useSEO, generateWebPageSchema, generateBreadcrumbSchema, generateServiceSchema } from '@/utils/seo';
import ServicePageLayout, { ServicePageConfig } from '@/pages/service-common/ServicePageLayout';

const COST_ROWS = [
  { label: '試算1', eng: '年収500万×20名', before: '1億円', after: '1,000万円', y1: '9千万円', y5: '4.5億円' },
  { label: '試算2', eng: '年収500万×40名', before: '2億円', after: '2,000万円', y1: '1.8億円', y5: '9億円' },
  { label: '試算3', eng: '年収500万×80名', before: '4億円', after: '4,000万円', y1: '3.6億円', y5: '18億円' },
  { label: '試算4', eng: '年収1,000万×20名', before: '2億円', after: '2,000万円', y1: '1.8億円', y5: '9億円' },
  { label: '試算5', eng: '年収1,000万×40名', before: '4億円', after: '4,000万円', y1: '3.6億円', y5: '18億円' },
  { label: '試算6', eng: '年収1,000万×80名', before: '8億円', after: '8,000万円', y1: '7.2億円', y5: '36億円' },
];

const CostTable = () => (
  <div className="mt-2">
    <div className="mb-6">
      <p className="text-xs tracking-widest uppercase font-medium mb-3" style={{ color: '#7C3AED' }}>ROI</p>
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">10倍以上の収益性改善を実現</h2>
      <p className="text-sm text-gray-500 leading-relaxed">システムエンジニアをAIで代替することにより、飛躍的なコスト削減が可能です。</p>
    </div>
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: 'linear-gradient(135deg, #0F172A, #1E1B4B)' }}>
            {['', '構成', '現状コスト', 'AI導入後', '1年削減', '5年削減'].map((h) => (
              <th key={h} className="px-4 py-3 text-left font-medium text-white/70 whitespace-nowrap text-xs tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {COST_ROWS.map((row, i) => (
            <tr key={i} className="border-t border-gray-100" style={{ background: i % 2 === 0 ? '#FAFAFA' : '#FFFFFF' }}>
              <td className="px-4 py-3 font-semibold text-xs" style={{ color: '#7C3AED' }}>{row.label}</td>
              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{row.eng}</td>
              <td className="px-4 py-3 font-semibold text-gray-900">{row.before}</td>
              <td className="px-4 py-3 font-semibold" style={{ color: '#7C3AED' }}>{row.after}</td>
              <td className="px-4 py-3 font-bold text-emerald-600">{row.y1}</td>
              <td className="px-4 py-3 font-bold text-emerald-700">{row.y5}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* ─── Hero Background ─── */
const HERO_IMG = 'https://readdy.ai/api/search-image?query=Elegant%20abstract%20artificial%20intelligence%20neural%20network%20with%20golden%20glowing%20nodes%20and%20connections%20on%20deep%20navy%20blue%20background%20minimal%20sophisticated%20high-end%20corporate%20aesthetic%20no%20text&width=1920&height=800&seq=aidevhero1&orientation=landscape';

const config: ServicePageConfig = {
  pageLabel: '生成AIコンサルティング・開発支援',
  heroImage: HERO_IMG,
  tag: 'AI Consulting & Development',
  title: <>生成AI活用<br />開発コンサルティング</>,
  subtitle: '貴社エンジニアチームと共に創る、次世代のシステム開発。生成AI活用により10倍以上の生産性向上・コスト削減を実現します。',
  icon: 'ri-robot-2-line',
  services: [
    { icon: 'ri-cpu-line', title: '生成AIによるDX推進', desc: '営業補助・問い合わせ対応・広告マーケティングなど多岐にわたる業務を生成AIで効率化。開発スピードは10倍以上。' },
    { icon: 'ri-team-line', title: 'AI人材育成', desc: '社員一人一人の生成AI活用能力を引き上げることで、会社全体の生産性が飛躍的に高まります。' },
    { icon: 'ri-rocket-line', title: '高速な事業開発', desc: '各種リサーチやアクションプランの作成など、人が数日かけた作業が数十分で完了します。' },
    { icon: 'ri-line-chart-line', title: 'コア事業の収益構造安定化', desc: '人的コスト削減と生産性向上により、収益構造を根本から変革し、安定した経営基盤を構築します。' },
    { icon: 'ri-compass-line', title: '新事業領域への挑戦', desc: '生成AIを活用した新規事業のワークフローを構築。少人数でも高い生産性を実現します。' },
    { icon: 'ri-shield-check-line', title: 'DX内製化支援', desc: '社内業務に生成AIを組み込んだDX化・内製化を支援。継続的な競争優位性を確立します。' },
  ],
  stats: [
    { number: '10x+', label: '開発生産性向上' },
    { number: '90%', label: 'コスト削減率' },
    { number: '30+', label: '特許取得数' },
    { number: 'No.1', label: 'Alexa SP 認定（日本初）' },
  ],
  benefits: [
    'エンジニアコストを最大90%削減、5年間で数十億円規模の削減効果',
    '生成AIによる開発スピード10倍以上の向上で、競合他社を圧倒',
    '社員全員がAIを使いこなせる組織に変革、全社的な生産性向上',
    '新規事業開発を高速化し、市場投入までの時間を大幅に短縮',
    '人に依存しない安定した収益構造を実現し、経営リスクを低減',
    '最先端の生成AI技術と豊富な実装ノウハウによる確実な成果',
  ],
  extraSection: <CostTable />,
};

export default function AIConsultingDevelopmentPage() {
  useSEO({
    title: '東京 生成AIコンサルティング・開発支援サービス TradFit株式会社',
    description: 'TradFit株式会社の生成AIコンサルティング・開発支援サービス。生成AI活用により10倍以上の生産性向上を実現。',
    keywords: '生成AI, AIコンサルティング, AI開発支援, DX推進, システム開発, 東京',
    path: '/ai-consulting-development',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('生成AIコンサルティング・開発支援サービス | TradFit株式会社', 'TradFit株式会社の生成AIコンサルティング・開発支援サービス。', '/ai-consulting-development'),
        generateBreadcrumbSchema([{ name: 'ホーム', path: '/' }, { name: '事業内容', path: '/business' }, { name: '生成AIコンサルティング・開発支援サービス', path: '/ai-consulting-development' }]),
        generateServiceSchema('生成AIコンサルティング・開発支援サービス', '貴社エンジニアチームと共に創る、次世代のシステム開発。'),
      ],
    },
  });
  return <ServicePageLayout config={config} />;
}
